import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Archive,
  Bot,
  Check,
  ChevronRight,
  AlertCircle,
  Loader2,
  Lock,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  Sparkles,
  Users,
} from 'lucide-react';
import {
  AvatarCustomerOption,
  AvatarProfile,
  AvatarProfileInput,
  AvatarProfileStatus,
  LiveAvatarCatalogEntry,
  useAdminApi,
} from '../hooks/useAdminApi';
import {
  avatarAssignmentFingerprint,
  avatarProfileFingerprint,
  filterLiveAvatarCatalog,
  formatAvatarAssignmentSummary,
  getAvatarCustomerSelectionState,
  isLiveAvatarCatalogSelection,
  liveAvatarSelectionValues,
  toggleAllAvatarCustomers,
} from '../utils/avatarProfiles';

interface SuperadminAvatarManagementProps {
  isDarkMode: boolean;
}

type AvatarDraft = AvatarProfileInput & {
  customer_ids: string[];
};

type SaveNotice = {
  tone: 'success' | 'warning' | 'error';
  message: string;
};

const EMPTY_DRAFT: AvatarDraft = {
  name: '',
  slug: '',
  description: '',
  role_label: '',
  provider_avatar_id: '',
  portrait_url: '',
  system_prompt: '',
  behavior_prompt: '',
  status: 'draft',
  customer_ids: [],
};

const statusLabel: Record<AvatarProfileStatus, string> = {
  draft: 'Entwurf',
  published: 'Veröffentlicht',
  archived: 'Archiviert',
};

const slugify = (value: string): string => value
  .trim()
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .slice(0, 64);

const customerIdOf = (customer: { id?: string; customer_id?: string }): string =>
  String(customer.id || customer.customer_id || '').trim();

const catalogSourceLabel = (source: LiveAvatarCatalogEntry['source']): string =>
  source === 'public' ? 'LiveAvatar Bibliothek' : 'Eigener Avatar';

const draftFromProfile = (profile: AvatarProfile): AvatarDraft => ({
  name: profile.name || '',
  slug: profile.slug || '',
  description: profile.description || '',
  role_label: profile.role_label || '',
  provider_avatar_id: profile.provider_avatar_id || '',
  portrait_url: profile.portrait_url || '',
  system_prompt: profile.system_prompt || '',
  behavior_prompt: profile.behavior_prompt || '',
  status: profile.status || 'draft',
  customer_ids: (profile.assigned_customers || []).map(customerIdOf).filter(Boolean),
});

function AvatarPortrait({ profile, large = false }: { profile: Pick<AvatarProfile, 'name' | 'portrait_url'>; large?: boolean }) {
  const [failed, setFailed] = useState(false);
  const sizeClasses = large ? 'h-24 w-24' : 'h-12 w-12';

  useEffect(() => setFailed(false), [profile.portrait_url]);

  return (
    <div className={`${sizeClasses} relative shrink-0 overflow-hidden rounded-xl border border-cyan-400/20 bg-slate-800`}>
      {!failed && profile.portrait_url ? (
        <img
          src={profile.portrait_url}
          alt={`${profile.name} Portrait`}
          className="h-full w-full object-cover"
          onError={() => setFailed(true)}
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center text-cyan-300">
          <Bot className={large ? 'h-9 w-9' : 'h-5 w-5'} aria-hidden="true" />
        </div>
      )}
    </div>
  );
}

const SuperadminAvatarManagement: React.FC<SuperadminAvatarManagementProps> = ({ isDarkMode }) => {
  const {
    error: apiError,
    getSuperadminAvatars,
    getLiveAvatarCatalog,
    createAvatarProfile,
    updateAvatarProfile,
    setAvatarCustomers,
  } = useAdminApi();
  const [profiles, setProfiles] = useState<AvatarProfile[]>([]);
  const [customers, setCustomers] = useState<AvatarCustomerOption[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<AvatarDraft>(EMPTY_DRAFT);
  const [isCreating, setIsCreating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [saveNotice, setSaveNotice] = useState<SaveNotice | null>(null);
  const [search, setSearch] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [catalog, setCatalog] = useState<LiveAvatarCatalogEntry[]>([]);
  const [catalogSearch, setCatalogSearch] = useState('');
  const [catalogGeneratedAt, setCatalogGeneratedAt] = useState<string | null>(null);
  const [isCatalogCached, setIsCatalogCached] = useState(false);
  const [isCatalogLoading, setIsCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const selectAllCustomersRef = useRef<HTMLInputElement>(null);
  const baselineProfileRef = useRef(avatarProfileFingerprint(EMPTY_DRAFT));
  const baselineAssignmentsRef = useRef(avatarAssignmentFingerprint(EMPTY_DRAFT));

  const selectedProfile = useMemo(
    () => profiles.find(profile => profile.id === selectedId) || null,
    [profiles, selectedId],
  );
  const isProtected = Boolean(selectedProfile?.is_system);
  const profileDirty = avatarProfileFingerprint(draft) !== baselineProfileRef.current;
  const assignmentsDirty = avatarAssignmentFingerprint(draft) !== baselineAssignmentsRef.current;
  const isDirty = !isProtected && (profileDirty || assignmentsDirty);

  const setBaseline = useCallback((nextDraft: AvatarDraft) => {
    baselineProfileRef.current = avatarProfileFingerprint(nextDraft);
    baselineAssignmentsRef.current = avatarAssignmentFingerprint(nextDraft);
  }, []);

  const confirmDiscardChanges = useCallback((): boolean => {
    if (!isDirty) return true;
    return window.confirm('Ungespeicherte Avatar-Änderungen verwerfen?');
  }, [isDirty]);

  const selectProfile = useCallback((profile: AvatarProfile) => {
    if (!confirmDiscardChanges()) return;
    const nextDraft = draftFromProfile(profile);
    setSelectedId(profile.id);
    setDraft(nextDraft);
    setBaseline(nextDraft);
    setIsCreating(false);
    setSlugEdited(true);
    setSaveNotice(null);
  }, [confirmDiscardChanges, setBaseline]);

  const loadProfiles = useCallback(async (preferredProfileId?: string) => {
    setIsLoading(true);
    setLoadError('');
    const payload = await getSuperadminAvatars();
    if (!payload) {
      setLoadError('Avatar-Profile konnten nicht geladen werden.');
      setIsLoading(false);
      return;
    }
    setProfiles(payload.profiles);
    setCustomers(payload.customers);
    setSelectedId(current => {
      const next = payload.profiles.find(profile => profile.id === preferredProfileId)
        || payload.profiles.find(profile => profile.id === current)
        || payload.profiles.find(profile => profile.is_system)
        || payload.profiles[0]
        || null;
      if (next) {
        const nextDraft = draftFromProfile(next);
        setDraft(nextDraft);
        setBaseline(nextDraft);
        setIsCreating(false);
        setSlugEdited(true);
      } else {
        setDraft(EMPTY_DRAFT);
        setBaseline(EMPTY_DRAFT);
      }
      return next?.id || null;
    });
    setIsLoading(false);
  }, [getSuperadminAvatars, setBaseline]);

  useEffect(() => {
    void loadProfiles();
  }, [loadProfiles]);

  const loadCatalog = useCallback(async (forceRefresh = false) => {
    setIsCatalogLoading(true);
    setCatalogError('');
    const payload = await getLiveAvatarCatalog(forceRefresh);
    if (!payload) {
      setCatalogError('Der LiveAvatar-Katalog ist gerade nicht erreichbar. Die bestehende Auswahl bleibt unverändert.');
      setIsCatalogLoading(false);
      return;
    }
    setCatalog(payload.avatars);
    setCatalogGeneratedAt(payload.generated_at);
    setIsCatalogCached(payload.cached);
    setIsCatalogLoading(false);
  }, [getLiveAvatarCatalog]);

  useEffect(() => {
    void loadCatalog();
  }, [loadCatalog]);

  const filteredProfiles = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return profiles;
    return profiles.filter(profile => [profile.name, profile.slug, profile.role_label, profile.status]
      .some(value => String(value || '').toLowerCase().includes(needle)));
  }, [profiles, search]);

  const eligibleCustomers = useMemo(() => {
    const seenIds = new Set<string>();
    return customers.filter(customer => {
      const customerId = customerIdOf(customer);
      if (!customerId || seenIds.has(customerId)) return false;
      seenIds.add(customerId);
      return true;
    });
  }, [customers]);

  const eligibleCustomerIds = useMemo(
    () => eligibleCustomers.map(customerIdOf),
    [eligibleCustomers],
  );

  const customerSelection = useMemo(
    () => getAvatarCustomerSelectionState(eligibleCustomerIds, draft.customer_ids),
    [draft.customer_ids, eligibleCustomerIds],
  );

  useEffect(() => {
    if (selectAllCustomersRef.current) {
      selectAllCustomersRef.current.indeterminate = customerSelection.isPartiallySelected;
    }
  }, [customerSelection.isPartiallySelected]);

  const filteredCustomers = useMemo(() => {
    const needle = customerSearch.trim().toLowerCase();
    if (!needle) return eligibleCustomers;
    return eligibleCustomers.filter(customer => [customer.name, customer.slug]
      .some(value => String(value || '').toLowerCase().includes(needle)));
  }, [customerSearch, eligibleCustomers]);

  const filteredCatalog = useMemo(
    () => filterLiveAvatarCatalog(catalog, catalogSearch),
    [catalog, catalogSearch],
  );

  const selectedCatalogAvatar = useMemo(
    () => catalog.find(avatar => isLiveAvatarCatalogSelection(avatar, draft.provider_avatar_id)) || null,
    [catalog, draft.provider_avatar_id],
  );

  const validationErrors = useMemo(() => {
    const messages: string[] = [];
    if (!draft.name.trim()) messages.push('Name fehlt');
    if (!draft.slug.trim()) messages.push('Slug fehlt');
    if (draft.slug && !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(draft.slug)) messages.push('Slug ist ungültig');
    if (draft.status === 'published' && (!draft.provider_avatar_id.trim() || !draft.portrait_url.trim())) {
      messages.push('LiveAvatar mit Vorschaubild fehlt');
    }
    return messages;
  }, [draft]);

  const beginCreate = () => {
    if (!confirmDiscardChanges()) return;
    setSelectedId(null);
    setDraft(EMPTY_DRAFT);
    setBaseline(EMPTY_DRAFT);
    setIsCreating(true);
    setSlugEdited(false);
    setSaveNotice(null);
  };

  const refreshProfiles = () => {
    if (!confirmDiscardChanges()) return;
    setSaveNotice(null);
    void loadProfiles();
  };

  useEffect(() => {
    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!isDirty) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [isDirty]);

  const updateDraft = <K extends keyof AvatarDraft>(key: K, value: AvatarDraft[K]) => {
    setDraft(current => ({ ...current, [key]: value }));
  };

  const handleNameChange = (name: string) => {
    setDraft(current => ({
      ...current,
      name,
      slug: slugEdited ? current.slug : slugify(name),
    }));
  };

  const selectCatalogAvatar = (avatar: LiveAvatarCatalogEntry) => {
    if (isProtected) return;
    const selection = liveAvatarSelectionValues(avatar);
    setDraft(current => ({ ...current, ...selection }));
  };

  const toggleCustomer = (customerId: string) => {
    if (isProtected || !customerId) return;
    setDraft(current => ({
      ...current,
      customer_ids: current.customer_ids.includes(customerId)
        ? current.customer_ids.filter(id => id !== customerId)
        : [...current.customer_ids, customerId],
    }));
  };

  const toggleAllCustomers = () => {
    if (isProtected || eligibleCustomerIds.length === 0) return;
    setDraft(current => ({
      ...current,
      customer_ids: toggleAllAvatarCustomers(eligibleCustomerIds, current.customer_ids),
    }));
  };

  const handleSave = async () => {
    if (isProtected || validationErrors.length > 0) return;
    setIsSaving(true);
    setSaveNotice(null);
    const input: AvatarProfileInput = {
      name: draft.name.trim(),
      slug: isCreating ? draft.slug.trim() : (selectedProfile?.slug || draft.slug.trim()),
      description: draft.description.trim(),
      role_label: draft.role_label.trim(),
      provider_avatar_id: draft.provider_avatar_id.trim(),
      portrait_url: draft.portrait_url.trim(),
      system_prompt: draft.system_prompt.trim(),
      behavior_prompt: draft.behavior_prompt.trim(),
      status: draft.status,
    };

    const savedProfile = isCreating
      ? await createAvatarProfile(input)
      : profileDirty && selectedId
        ? await updateAvatarProfile(selectedId, input)
        : selectedProfile;

    if (!savedProfile) {
      setSaveNotice({ tone: 'error', message: 'Speichern fehlgeschlagen. Bitte Eingaben und Verbindung prüfen.' });
      setIsSaving(false);
      return;
    }

    const assignedProfile = (isCreating || assignmentsDirty)
      ? await setAvatarCustomers(savedProfile.id, draft.customer_ids)
      : savedProfile;
    if (!assignedProfile) {
      setIsSaving(false);
      await loadProfiles(savedProfile.id);
      setSaveNotice({ tone: 'warning', message: 'Profil gespeichert, aber Kundenfreigaben konnten nicht übernommen werden.' });
      return;
    }

    await loadProfiles(savedProfile.id);
    setIsCreating(false);
    setSaveNotice({ tone: 'success', message: 'Avatar / Agent und Kundenfreigaben gespeichert.' });
    setIsSaving(false);
  };

  const fieldClasses = isDarkMode
    ? 'border-slate-700 bg-slate-950 text-slate-100 placeholder:text-slate-600 focus:border-cyan-400 focus:ring-cyan-400/15 disabled:bg-slate-900 disabled:text-slate-500'
    : 'border-slate-300 bg-white text-slate-900 placeholder:text-slate-400 focus:border-cyan-600 focus:ring-cyan-600/10 disabled:bg-slate-100 disabled:text-slate-500';
  const panelClasses = isDarkMode ? 'border-slate-800 bg-slate-900' : 'border-slate-200 bg-white';
  const mutedClasses = isDarkMode ? 'text-slate-400' : 'text-slate-500';

  if (isLoading && profiles.length === 0) {
    return (
      <section className={`flex min-h-[420px] items-center justify-center rounded-xl border ${panelClasses}`} role="status">
        <div className="text-center">
          <Loader2 className="mx-auto h-7 w-7 animate-spin text-cyan-500" />
          <p className={`mt-3 text-sm ${mutedClasses}`}>Avatar-Profile werden geladen…</p>
        </div>
      </section>
    );
  }

  if (loadError && profiles.length === 0) {
    return (
      <section className={`rounded-xl border p-8 text-center ${panelClasses}`}>
        <AlertCircle className="mx-auto h-8 w-8 text-amber-500" />
        <h2 className="mt-3 text-base font-semibold">Avatare nicht erreichbar</h2>
        <p className={`mx-auto mt-2 max-w-lg text-sm ${mutedClasses}`}>{loadError}</p>
        <button type="button" onClick={refreshProfiles} className="mt-5 inline-flex items-center gap-2 rounded-lg bg-cyan-600 px-4 py-2 text-sm font-semibold text-white hover:bg-cyan-500">
          <RefreshCw className="h-4 w-4" /> Erneut laden
        </button>
      </section>
    );
  }

  return (
    <section aria-label="Avatar-Verwaltung" className="space-y-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-cyan-500" />
            <p className={`text-xs font-semibold uppercase tracking-[0.18em] ${mutedClasses}`}>Avatar Library</p>
          </div>
          <h2 className="mt-2 text-2xl font-semibold tracking-tight">Digitale Experten verwalten</h2>
          <p className={`mt-1 max-w-2xl text-sm ${mutedClasses}`}>
            Avatar / Agent-Vorlagen erstellen, Verhalten definieren und auf vollständige Kundenkonten freischalten.
          </p>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={refreshProfiles} disabled={isLoading} className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold transition disabled:opacity-50 ${isDarkMode ? 'border-slate-700 text-slate-300 hover:border-slate-500 hover:text-white' : 'border-slate-300 text-slate-600 hover:border-slate-400 hover:text-slate-900'}`}>
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} /> Aktualisieren
          </button>
          <button type="button" onClick={beginCreate} className="inline-flex items-center gap-2 rounded-lg bg-cyan-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:ring-offset-2 focus:ring-offset-slate-900">
            <Plus className="h-4 w-4" /> Neuer Avatar / Agent
          </button>
        </div>
      </div>

      {(loadError || apiError) ? (
        <div className={`flex items-start gap-2 rounded-lg border px-4 py-3 text-sm ${isDarkMode ? 'border-amber-800/60 bg-amber-950/30 text-amber-100' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{loadError || apiError}</span>
        </div>
      ) : null}

      <div className="grid gap-5 xl:grid-cols-[minmax(280px,0.72fr)_minmax(0,1.65fr)]">
        <aside className={`overflow-hidden rounded-xl border ${panelClasses}`}>
          <div className={`border-b p-4 ${isDarkMode ? 'border-slate-800' : 'border-slate-200'}`}>
            <div className="flex items-center justify-between gap-3">
              <div>
                  <h3 className="text-sm font-semibold">Avatar / Agenten</h3>
                <p className={`mt-0.5 text-xs ${mutedClasses}`}>{profiles.length} Einträge</p>
              </div>
              <span className={`rounded-md border px-2 py-1 text-xs font-medium ${isDarkMode ? 'border-slate-700 text-slate-400' : 'border-slate-200 text-slate-500'}`}>
                {profiles.filter(profile => profile.status === 'published').length} live
              </span>
            </div>
            <label className="relative mt-3 block">
              <Search className={`pointer-events-none absolute left-3 top-2.5 h-4 w-4 ${mutedClasses}`} />
              <span className="sr-only">Avatar-Profile durchsuchen</span>
              <input value={search} onChange={event => setSearch(event.target.value)} placeholder="Name, Rolle oder Status" className={`w-full rounded-lg border py-2 pl-9 pr-3 text-sm outline-none ring-2 ring-transparent transition ${fieldClasses}`} />
            </label>
          </div>

          <div className="max-h-[720px] divide-y divide-slate-800/70 overflow-y-auto">
            {filteredProfiles.map(profile => {
              const selected = !isCreating && selectedId === profile.id;
              const assignedNames = (profile.assigned_customers || [])
                .map(customer => customer.name || customer.slug)
                .filter(Boolean);
              const customerSummary = formatAvatarAssignmentSummary(profile);
              return (
                <button
                  type="button"
                  key={profile.id}
                  onClick={() => selectProfile(profile)}
                  className={`flex w-full items-center gap-3 p-4 text-left transition focus:outline-none focus:ring-2 focus:ring-inset focus:ring-cyan-500 ${selected ? (isDarkMode ? 'bg-cyan-950/45' : 'bg-cyan-50') : (isDarkMode ? 'hover:bg-slate-800/70' : 'hover:bg-slate-50')}`}
                >
                  <AvatarPortrait profile={profile} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-semibold">{profile.name}</span>
                      {profile.is_system ? <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-cyan-500" aria-label="Geschützt" /> : null}
                    </div>
                    <p className={`mt-0.5 truncate text-xs ${mutedClasses}`}>{profile.role_label || profile.slug}</p>
                    <div className="mt-2 flex items-center gap-2 text-[11px]">
                      <span className={profile.status === 'published' ? 'text-emerald-500' : profile.status === 'archived' ? 'text-slate-500' : 'text-amber-500'}>{statusLabel[profile.status]}</span>
                      <span className={mutedClasses}>·</span>
                      <span className={mutedClasses}>{profile.is_system ? 'Alle Kunden' : `${profile.assigned_customers?.length || 0} Kunden`}</span>
                    </div>
                    {!profile.is_system && assignedNames.length > 0 ? (
                      <p className={`mt-1 truncate text-[11px] ${mutedClasses}`} title={assignedNames.join(', ')}>
                        {customerSummary}
                      </p>
                    ) : null}
                  </div>
                  <ChevronRight className={`h-4 w-4 shrink-0 ${selected ? 'text-cyan-500' : mutedClasses}`} />
                </button>
              );
            })}
            {filteredProfiles.length === 0 ? (
              <div className="p-8 text-center">
                <Bot className={`mx-auto h-7 w-7 ${mutedClasses}`} />
                <p className={`mt-3 text-sm ${mutedClasses}`}>Keine passenden Profile.</p>
              </div>
            ) : null}
          </div>
        </aside>

        <div className={`rounded-xl border ${panelClasses}`}>
          <div className={`flex flex-col gap-4 border-b p-5 sm:flex-row sm:items-center sm:justify-between ${isDarkMode ? 'border-slate-800' : 'border-slate-200'}`}>
            <div className="flex items-center gap-4">
              <AvatarPortrait profile={{ name: draft.name || 'Neuer Avatar', portrait_url: draft.portrait_url }} large />
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-lg font-semibold">{isCreating ? 'Neue Avatar / Agent-Vorlage' : (selectedProfile?.name || 'Avatar auswählen')}</h3>
                  {isProtected ? (
                    <span className="inline-flex items-center gap-1 rounded-md border border-cyan-500/25 bg-cyan-500/10 px-2 py-1 text-[11px] font-semibold text-cyan-500">
                      <Lock className="h-3 w-3" /> Standard · geschützt
                    </span>
                  ) : null}
                </div>
                <p className={`mt-1 text-xs ${mutedClasses}`}>
                  {isProtected ? 'Sally bleibt global verfügbar und kann nicht verändert oder archiviert werden.' : isCreating ? 'Erstellt eine globale Agent-Vorlage; Kundenfreigaben provisionieren echte, isolierte Agenten.' : `Version ${selectedProfile?.version || 1} · ${selectedProfile?.slug || ''}`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!isProtected && !isCreating && draft.status !== 'archived' ? (
                <button type="button" onClick={() => updateDraft('status', 'archived')} className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-semibold transition ${isDarkMode ? 'border-slate-700 text-slate-400 hover:border-amber-600 hover:text-amber-300' : 'border-slate-300 text-slate-600 hover:border-amber-400 hover:text-amber-700'}`}>
                  <Archive className="h-3.5 w-3.5" /> Zur Archivierung vormerken
                </button>
              ) : null}
            </div>
          </div>

          <div className="space-y-7 p-5">
            <fieldset disabled={isProtected || (!isCreating && !selectedProfile)} className="space-y-7 disabled:opacity-80">
              <div>
                <h4 className="text-sm font-semibold">Profil & Provider</h4>
                <p className={`mt-1 text-xs ${mutedClasses}`}>Sichtbare Identität und technische HeyGen/LiveAvatar-Verknüpfung.</p>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <label className="text-xs font-medium">Name *
                    <input value={draft.name} onChange={event => handleNameChange(event.target.value)} className={`mt-1.5 w-full rounded-lg border px-3 py-2.5 text-sm outline-none ring-2 ring-transparent transition ${fieldClasses}`} placeholder="z. B. Alex" />
                  </label>
                  <label className="text-xs font-medium">Slug *
                    <input value={draft.slug} readOnly={!isCreating} onChange={event => { setSlugEdited(true); updateDraft('slug', slugify(event.target.value)); }} className={`mt-1.5 w-full rounded-lg border px-3 py-2.5 font-mono text-sm outline-none ring-2 ring-transparent transition ${fieldClasses}`} placeholder="alex-consulting" />
                    {!isCreating ? <span className={`mt-1 block text-[11px] font-normal ${mutedClasses}`}>Bleibt stabil, damit URLs und Agentdaten erhalten bleiben.</span> : null}
                  </label>
                  <label className="text-xs font-medium">Rollenbezeichnung
                    <input value={draft.role_label} onChange={event => updateDraft('role_label', event.target.value)} className={`mt-1.5 w-full rounded-lg border px-3 py-2.5 text-sm outline-none ring-2 ring-transparent transition ${fieldClasses}`} placeholder="Sales & Marketing" />
                  </label>
                  <label className="text-xs font-medium sm:col-span-2">Kurzbeschreibung
                    <textarea value={draft.description} onChange={event => updateDraft('description', event.target.value)} rows={2} className={`mt-1.5 w-full resize-y rounded-lg border px-3 py-2.5 text-sm leading-6 outline-none ring-2 ring-transparent transition ${fieldClasses}`} placeholder="Wofür dieser Avatar eingesetzt wird." />
                  </label>
                </div>

                <div className={`mt-5 overflow-hidden rounded-xl border ${isDarkMode ? 'border-slate-700 bg-slate-950/45' : 'border-slate-200 bg-slate-50/80'}`}>
                  <div className={`border-b p-4 ${isDarkMode ? 'border-slate-800' : 'border-slate-200'}`}>
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <h5 className="text-sm font-semibold">LiveAvatar auswählen</h5>
                          {selectedCatalogAvatar ? (
                            <span className="inline-flex items-center gap-1 rounded-md border border-cyan-500/30 bg-cyan-500/10 px-2 py-0.5 text-[11px] font-semibold text-cyan-500">
                              <Check className="h-3 w-3" /> Ausgewählt
                            </span>
                          ) : null}
                        </div>
                        <p className={`mt-1 text-xs leading-5 ${mutedClasses}`}>
                          Die Auswahl übernimmt Provider-ID und Vorschaubild in dieses Profil. Greenscreen-Varianten werden ausgeblendet. Gespeichert wird erst unten.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => void loadCatalog(true)}
                        disabled={isCatalogLoading}
                        className={`inline-flex min-h-10 shrink-0 items-center justify-center gap-2 rounded-lg border px-3 py-2 text-xs font-semibold transition disabled:cursor-wait disabled:opacity-60 ${isDarkMode ? 'border-slate-700 text-slate-300 hover:border-cyan-600 hover:text-cyan-300' : 'border-slate-300 text-slate-600 hover:border-cyan-500 hover:text-cyan-700'}`}
                      >
                        <RefreshCw className={`h-3.5 w-3.5 ${isCatalogLoading ? 'animate-spin' : ''}`} />
                        Katalog aktualisieren
                      </button>
                    </div>

                    <label className="relative mt-3 block">
                      <Search className={`pointer-events-none absolute left-3 top-3 h-4 w-4 ${mutedClasses}`} />
                      <span className="sr-only">LiveAvatare durchsuchen</span>
                      <input
                        value={catalogSearch}
                        onChange={event => setCatalogSearch(event.target.value)}
                        placeholder="Name, Avatar-ID, Typ oder Stimme"
                        className={`min-h-10 w-full rounded-lg border py-2 pl-9 pr-3 text-sm outline-none ring-2 ring-transparent transition ${fieldClasses}`}
                      />
                    </label>
                  </div>

                  <div className="max-h-80 overflow-y-auto p-3" aria-live="polite">
                    {catalogError && catalog.length > 0 ? (
                      <div className={`mb-3 flex items-start gap-2 rounded-lg border px-3 py-2 text-xs ${isDarkMode ? 'border-amber-800/70 bg-amber-950/20 text-amber-100' : 'border-amber-300 bg-amber-50 text-amber-800'}`}>
                        <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        <span>Aktualisierung fehlgeschlagen. Der zuletzt geladene Katalog bleibt verfügbar.</span>
                      </div>
                    ) : null}
                    {isCatalogLoading && catalog.length === 0 ? (
                      <div className="flex min-h-36 items-center justify-center text-center" role="status">
                        <div>
                          <Loader2 className="mx-auto h-6 w-6 animate-spin text-cyan-500" />
                          <p className={`mt-2 text-xs ${mutedClasses}`}>Verfügbare LiveAvatare werden geladen…</p>
                        </div>
                      </div>
                    ) : catalogError && catalog.length === 0 ? (
                      <div className={`flex min-h-36 flex-col items-center justify-center rounded-lg border border-dashed p-4 text-center ${isDarkMode ? 'border-amber-800/70 bg-amber-950/20' : 'border-amber-300 bg-amber-50'}`}>
                        <AlertCircle className="h-6 w-6 text-amber-500" />
                        <p className="mt-2 max-w-md text-sm font-semibold">Katalog nicht erreichbar</p>
                        <p className={`mt-1 max-w-md text-xs leading-5 ${mutedClasses}`}>{catalogError}</p>
                        <button type="button" onClick={() => void loadCatalog(true)} className="mt-3 inline-flex min-h-10 items-center gap-2 rounded-lg bg-cyan-600 px-3 py-2 text-xs font-semibold text-white hover:bg-cyan-500">
                          <RefreshCw className="h-3.5 w-3.5" /> Erneut versuchen
                        </button>
                      </div>
                    ) : filteredCatalog.length > 0 ? (
                      <div className="grid gap-2 lg:grid-cols-2">
                        {filteredCatalog.map(avatar => {
                          const selected = isLiveAvatarCatalogSelection(avatar, draft.provider_avatar_id);
                          return (
                            <button
                              type="button"
                              key={avatar.id}
                              onClick={() => selectCatalogAvatar(avatar)}
                              aria-pressed={selected}
                              className={`group flex min-h-[76px] w-full items-center gap-3 rounded-lg border p-2.5 text-left outline-none transition focus:ring-2 focus:ring-cyan-500 ${selected ? (isDarkMode ? 'border-cyan-500 bg-cyan-950/55 shadow-[inset_3px_0_0_#06b6d4]' : 'border-cyan-500 bg-cyan-50 shadow-[inset_3px_0_0_#0891b2]') : (isDarkMode ? 'border-slate-800 bg-slate-900/70 hover:border-slate-600' : 'border-slate-200 bg-white hover:border-slate-300')}`}
                            >
                              <AvatarPortrait profile={{ name: avatar.name, portrait_url: avatar.preview_url }} />
                              <span className="min-w-0 flex-1">
                                <span className="flex items-start justify-between gap-2">
                                  <span className="truncate text-sm font-semibold">{avatar.name}</span>
                                  {selected ? <Check className="h-4 w-4 shrink-0 text-cyan-500" aria-hidden="true" /> : null}
                                </span>
                                <span className="mt-1 flex flex-wrap items-center gap-1.5">
                                  <span className={`rounded border px-1.5 py-0.5 text-[10px] font-semibold ${avatar.source === 'public' ? (isDarkMode ? 'border-slate-700 text-slate-400' : 'border-slate-200 text-slate-500') : 'border-cyan-500/30 text-cyan-500'}`}>
                                    {catalogSourceLabel(avatar.source)}
                                  </span>
                                  {avatar.is_1080p ? <span className={`text-[10px] ${mutedClasses}`}>1080p</span> : null}
                                  {avatar.type ? <span className={`truncate text-[10px] ${mutedClasses}`}>{avatar.type}</span> : null}
                                </span>
                                {avatar.default_voice ? (
                                  <span className={`mt-1 block truncate text-[10px] ${mutedClasses}`}>Stimme: {avatar.default_voice}</span>
                                ) : null}
                                <span className={`mt-1 block truncate font-mono text-[10px] ${mutedClasses}`}>{avatar.id}</span>
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="flex min-h-32 flex-col items-center justify-center text-center">
                        <Bot className={`h-6 w-6 ${mutedClasses}`} />
                        <p className="mt-2 text-sm font-semibold">
                          {catalog.length === 0 ? 'Keine aktiven LiveAvatare verfügbar' : 'Keine Treffer'}
                        </p>
                        <p className={`mt-1 text-xs ${mutedClasses}`}>
                          {catalog.length === 0 ? 'Veröffentlichte Vorlagen behalten ihre bestehende Auswahl; für Änderungen wird der Katalog benötigt.' : 'Passe den Suchbegriff an.'}
                        </p>
                      </div>
                    )}
                  </div>

                  {catalogGeneratedAt ? (
                    <div className={`border-t px-4 py-2 text-[10px] ${isDarkMode ? 'border-slate-800 text-slate-500' : 'border-slate-200 text-slate-500'}`}>
                      {catalog.length} aktive Avatare · {isCatalogCached ? 'Zwischengespeicherter Katalog' : 'Gerade vom Provider geladen'}
                    </div>
                  ) : null}
                </div>

                <div className={`mt-3 rounded-lg border px-4 py-3 ${isDarkMode ? 'border-slate-800 bg-slate-950/30' : 'border-slate-200 bg-slate-50'}`}>
                  <p className="text-xs font-semibold">Provider Avatar ID</p>
                  <p className={`mt-1 break-all font-mono text-xs ${mutedClasses}`}>{draft.provider_avatar_id || 'Noch kein LiveAvatar ausgewählt'}</p>
                  <p className={`mt-2 text-[11px] leading-5 ${mutedClasses}`}>Gesicht, Dashboardbild und Livestream werden immer gemeinsam über die Katalogauswahl geändert.</p>
                </div>
              </div>

              <div className={`border-t pt-6 ${isDarkMode ? 'border-slate-800' : 'border-slate-200'}`}>
                <h4 className="text-sm font-semibold">Persönlichkeit & Verhalten</h4>
                <p className={`mt-1 text-xs ${mutedClasses}`}>Definiert die globale Grundidentität. Kundenspezifische Prompts und Wissensdaten bleiben pro Agent vollständig isoliert.</p>
                <div className="mt-4 grid gap-4 lg:grid-cols-2">
                  <label className="text-xs font-medium">Systemprompt
                    <textarea value={draft.system_prompt} onChange={event => updateDraft('system_prompt', event.target.value)} rows={9} className={`mt-1.5 w-full resize-y rounded-lg border px-3 py-2.5 font-mono text-xs leading-5 outline-none ring-2 ring-transparent transition ${fieldClasses}`} placeholder="Identität, Ziel und Grenzen des Avatars…" />
                  </label>
                  <label className="text-xs font-medium">Behaviour Prompt
                    <textarea value={draft.behavior_prompt} onChange={event => updateDraft('behavior_prompt', event.target.value)} rows={9} className={`mt-1.5 w-full resize-y rounded-lg border px-3 py-2.5 font-mono text-xs leading-5 outline-none ring-2 ring-transparent transition ${fieldClasses}`} placeholder="Ton, Gesprächsführung und gewünschtes Verhalten…" />
                  </label>
                </div>
              </div>

              <div className={`border-t pt-6 ${isDarkMode ? 'border-slate-800' : 'border-slate-200'}`}>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-cyan-500" />
                      <h4 className="text-sm font-semibold">Kundenfreigaben</h4>
                    </div>
                    <p className={`mt-1 text-xs ${mutedClasses}`}>Die Auswahl gilt immer für das gesamte Kundenkonto.</p>
                  </div>
                  <label className="relative block sm:w-64">
                    <Search className={`pointer-events-none absolute left-3 top-2.5 h-4 w-4 ${mutedClasses}`} />
                    <span className="sr-only">Kunden durchsuchen</span>
                    <input value={customerSearch} onChange={event => setCustomerSearch(event.target.value)} placeholder="Kunden suchen" className={`w-full rounded-lg border py-2 pl-9 pr-3 text-sm outline-none ring-2 ring-transparent transition ${fieldClasses}`} />
                  </label>
                </div>

                {isProtected ? (
                  <div className={`mt-4 flex items-start gap-3 rounded-lg border p-4 ${isDarkMode ? 'border-cyan-900/70 bg-cyan-950/25' : 'border-cyan-200 bg-cyan-50'}`}>
                    <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-cyan-500" />
                    <div>
                      <p className="text-sm font-semibold">Für alle Kunden freigeschaltet</p>
                      <p className={`mt-1 text-xs leading-5 ${mutedClasses}`}>Sally ist der globale Fallback. Diese Freigabe kann nicht entfernt werden.</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className={`mt-4 flex flex-col gap-3 rounded-lg border px-3 py-3 sm:flex-row sm:items-center sm:justify-between ${isDarkMode ? 'border-slate-800 bg-slate-950/35' : 'border-slate-200 bg-slate-50'}`}>
                      <label className={`group flex min-w-0 items-center gap-3 rounded-md outline-none focus-within:ring-2 focus-within:ring-cyan-500 focus-within:ring-offset-2 ${isDarkMode ? 'focus-within:ring-offset-slate-900' : 'focus-within:ring-offset-white'} ${customerSelection.totalCount === 0 ? 'cursor-not-allowed opacity-55' : 'cursor-pointer'}`}>
                        <input
                          ref={selectAllCustomersRef}
                          type="checkbox"
                          checked={customerSelection.isAllSelected}
                          disabled={customerSelection.totalCount === 0}
                          onChange={toggleAllCustomers}
                          aria-describedby="avatar-customer-selection-scope"
                          className="sr-only"
                        />
                        <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border transition ${customerSelection.isAllSelected || customerSelection.isPartiallySelected ? 'border-cyan-500 bg-cyan-500 text-slate-950' : (isDarkMode ? 'border-slate-600 group-hover:border-slate-500' : 'border-slate-300 group-hover:border-slate-400')}`} aria-hidden="true">
                          {customerSelection.isAllSelected ? <Check className="h-3.5 w-3.5" /> : null}
                          {customerSelection.isPartiallySelected ? <span className="h-0.5 w-2.5 rounded bg-slate-950" /> : null}
                        </span>
                        <span className="text-sm font-semibold">
                          {customerSelection.isAllSelected ? 'Alle abwählen' : 'Alle Kunden auswählen'}
                        </span>
                      </label>
                      <p id="avatar-customer-selection-scope" className={`text-xs ${mutedClasses}`}>
                        {customerSelection.totalCount === 0
                          ? 'Keine gültigen Kunden verfügbar'
                          : `Gesamte Kundenliste · ${customerSelection.selectedCount}/${customerSelection.totalCount} ausgewählt`}
                      </p>
                    </div>

                    <div className="mt-3 grid max-h-64 gap-2 overflow-y-auto pr-1 sm:grid-cols-2">
                      {filteredCustomers.map(customer => {
                        const customerId = customerIdOf(customer);
                        const checked = draft.customer_ids.includes(customerId);
                        return (
                          <label key={customerId} className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition focus-within:ring-2 focus-within:ring-cyan-500 ${checked ? (isDarkMode ? 'border-cyan-600/60 bg-cyan-950/30' : 'border-cyan-300 bg-cyan-50') : (isDarkMode ? 'border-slate-800 hover:border-slate-700' : 'border-slate-200 hover:border-slate-300')}`}>
                            <input type="checkbox" checked={checked} onChange={() => toggleCustomer(customerId)} className="sr-only" />
                            <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${checked ? 'border-cyan-500 bg-cyan-500 text-slate-950' : (isDarkMode ? 'border-slate-600' : 'border-slate-300')}`}>
                              {checked ? <Check className="h-3.5 w-3.5" /> : null}
                            </span>
                            <span className="min-w-0">
                              <span className="block truncate text-sm font-medium">{customer.name}</span>
                              <span className={`block truncate text-xs ${mutedClasses}`}>{customer.slug}</span>
                            </span>
                          </label>
                        );
                      })}
                      {filteredCustomers.length === 0 ? (
                        <p className={`py-4 text-sm ${mutedClasses}`}>
                          {customerSelection.totalCount === 0 ? 'Keine Kunden verfügbar.' : 'Keine Kunden gefunden.'}
                        </p>
                      ) : null}
                    </div>
                  </>
                )}
              </div>

              <div className={`border-t pt-6 ${isDarkMode ? 'border-slate-800' : 'border-slate-200'}`}>
                <label className="block max-w-xs text-xs font-medium">Status
                  <select value={draft.status} onChange={event => updateDraft('status', event.target.value as AvatarProfileStatus)} className={`mt-1.5 w-full rounded-lg border px-3 py-2.5 text-sm outline-none ring-2 ring-transparent transition ${fieldClasses}`}>
                    <option value="draft">Entwurf – nicht sichtbar</option>
                    <option value="published">Veröffentlicht – für Kunden sichtbar</option>
                    {!isCreating ? <option value="archived">Archiviert</option> : null}
                  </select>
                </label>
              </div>
            </fieldset>

            {!isProtected && (isCreating || selectedProfile) ? (
              <div className={`flex flex-col gap-3 border-t pt-5 sm:flex-row sm:items-center sm:justify-between ${isDarkMode ? 'border-slate-800' : 'border-slate-200'}`}>
                <div className="min-h-5 text-xs">
                  {validationErrors.length ? (
                    <span className="text-amber-500">Noch offen: {validationErrors.join(', ')}.</span>
                  ) : saveNotice ? (
                    <span className={saveNotice.tone === 'success' ? 'text-emerald-500' : saveNotice.tone === 'error' ? 'text-red-500' : 'text-amber-500'}>
                      {saveNotice.message}
                    </span>
                  ) : isDirty ? (
                    <span className="font-semibold text-amber-500">Ungespeicherte Änderungen</span>
                  ) : (
                    <span className={mutedClasses}>{customerSelection.selectedCount} Kunden ausgewählt</span>
                  )}
                </div>
                <button type="button" onClick={() => void handleSave()} disabled={isSaving || validationErrors.length > 0 || !isDirty} className="inline-flex min-w-40 items-center justify-center gap-2 rounded-lg bg-cyan-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-cyan-500 disabled:cursor-not-allowed disabled:opacity-50">
                  {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                  {isSaving ? 'Speichert…' : isCreating ? 'Avatar / Agent erstellen' : 'Änderungen speichern'}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SuperadminAvatarManagement;
